import { FC, useRef, useState } from "react"
import { Button, Divider, Flex, Paper } from "@mantine/core"
import { useMediaQuery } from "@mantine/hooks"
import { useForm } from "@mantine/form"
import { Link, useLocation } from "react-router-dom"

import { useAppDispatch, useAppSelector } from "@/shared/lib/store"

import { FilterInputList } from "@/features/Filter/ui/FilterInputList"
import { FilterFormValues } from "@/features/Filter/lib"
import { setTotal } from "@/features/Filter/model/filterSlice"
import { selectFilterTotal } from "@/features/Filter/model/selectors"

import { IFilterInput } from "@/features/Filter/lib/helpers/getFiltersInputs"
import { getFilterText } from "@/features/Filter/lib/helpers/getFilterText"
import { generateQueryParams } from "@/features/Filter/lib/helpers/generateQueryStringFromValues"

import IconClose from "@/shared/assets/icons/icon__close.svg?react"
import classes from "./styles.module.scss"
import { getFeedCount } from "@/entities/feed"

interface FilterProps {
	filterInputs: IFilterInput[]
	onSubmitButtonClick?: () => void
}

export const Filter: FC<FilterProps> = ({ filterInputs, onSubmitButtonClick }) => {
	const timeoutKey = useRef<NodeJS.Timeout | null>(null)
	const [searchParams, setSearchParams] = useState<URLSearchParams>(new URLSearchParams())
	const { pathname } = useLocation()
	const [isLoading, setIsLoading] = useState(false)
	const total = useAppSelector(selectFilterTotal)
	const dispatch = useAppDispatch()
	const isSmallMobile = useMediaQuery("(max-width: 580px)")

	const form = useForm<FilterFormValues>({
		mode: "uncontrolled",
		initialValues: {
			...filterInputs.reduce((acc: Record<string, string | [number, number]>, input) => {
				acc[input.name] = input.type === "options" ? "" : input.range?.defaultValue ?? ""
				return acc
			}, {}),
		},
		onValuesChange: async values => {
			setIsLoading(true)
			const params = generateQueryParams(values)
			timeoutKey.current && clearTimeout(timeoutKey.current)
			timeoutKey.current = setTimeout(async () => {
				const data = await getFeedCount(params.toString())
				setSearchParams(params)
				dispatch(setTotal(data))
				setIsLoading(false)
			}, 1000)
		},
	})

	return (
		<Paper bg="gray.0" radius={10}>
			<form>
				<Flex py={24} px={16}>
					<FilterInputList form={form} inputs={filterInputs} />
				</Flex>
				<Divider color="gray.1"></Divider>
				<Flex
					p={16}
					justify="space-between"
					align="center"
					wrap="wrap"
					gap={16}
					direction={isSmallMobile ? "column" : "row"}
				>
					<Button
						variant="transparent"
						fw={400}
						fz={16}
						c="dark.3"
						size="md"
						p={0}
						rightSection={<IconClose width={24} height={24} />}
						onClick={() => {
							if (form.isDirty()) form.reset()
						}}
					>
						Очистить фильтр
					</Button>
					<Link
						to={`${pathname}${form.isDirty() ? `?${searchParams}` : ""}`}
						className={isSmallMobile ? classes.maxWidth : ""}
					>
						<Button
							size="md"
							h={50}
							fw={600}
							fz={16}
							loaderProps={{ type: "dots" }}
							type="submit"
							disabled={!total}
							loading={isLoading}
							w={isSmallMobile ? "100%" : "auto"}
							onClick={onSubmitButtonClick}
						>
							{getFilterText(Number(total))}
						</Button>
					</Link>
				</Flex>
			</form>
		</Paper>
	)
}
